import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Paper,
  IconButton,
  Stack,
  Link,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Close as CloseIcon,
  AttachFile as AttachFileIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { apiClient } from "../../utils/apiClient";
import { useDispatch } from "react-redux";
import { openEmailModal, openResultDrawer } from "../../store/adminSlice";
import { DiagnosticEventsDisplay } from "../emailAutomationPipeline/components/DiagnosticEventsDisplay";
import type { ActionDetailApi } from "../emailAutomationPipeline/components/types";

interface ActionDetailModalProps {
  actionId: string | null;
  open: boolean;
  onClose: () => void;
}

const EXECUTION_STATE_COLORS: Record<
  string,
  "default" | "warning" | "info" | "success" | "error"
> = {
  READY: "warning",
  TEST: "default",
  RUN: "info",
  SENT: "success",
  FAILED: "error",
};

export function AutomationActionDetailModal({
  actionId,
  open,
  onClose,
}: ActionDetailModalProps) {
  const [action, setAction] = useState<ActionDetailApi | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (open && actionId) {
      fetchAction(actionId);
    } else {
      setAction(null);
      setError(null);
    }
  }, [open, actionId]);

  const fetchAction = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<ActionDetailApi>(`/admin/email-automation/actions/${id}`);
      setAction(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load action");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailClick = (emailId: string) => {
    dispatch(openEmailModal(emailId));
  };

  const handleAttachmentClick = async (attachmentId: string) => {
    if (!actionId) return;
    try {
      const res = await apiClient.get<{ downloadUrl: string }>(
        `/admin/email-automation/actions/${actionId}/attachment/${attachmentId}`,
      );
      window.open(res.downloadUrl, "_blank");
    } catch (e) {
      console.error("Failed to download attachment:", e);
    }
  };

  const data = action?.data ?? {};
  const messageContent = data.messageContent as string | undefined;
  const replyToEmailId = data.replyToEmailId as string | undefined;
  const replyToEmailSubject = data.replyToEmailSubject as string | undefined;
  const subject = data.subject as string | undefined;
  const resolvedAttachments = data.resolvedAttachments as
    | Array<{ attachmentId: string; filename: string }>
    | undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: "80vh" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6">
            {action
              ? action.actionType === "draftEmail"
                ? "Draft Email"
                : action.actionType
              : "Action Detail"}
          </Typography>
          {action && (
            <Chip
              label={action.executionState}
              size="small"
              color={
                EXECUTION_STATE_COLORS[action.executionState] || "default"
              }
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box
            sx={{ display: "flex", justifyContent: "center", p: 4 }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ p: 2 }}>
            {error}
          </Typography>
        )}

        {action && !loading && (
          <Stack spacing={2}>
            {/* Subject */}
            {(subject || replyToEmailSubject) && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  Subject
                </Typography>
                <Typography variant="body2">
                  {subject || replyToEmailSubject}
                </Typography>
              </Box>
            )}

            {/* Reply-to email */}
            {replyToEmailId && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  Reply to email
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <EmailIcon fontSize="small" color="primary" />
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => handleEmailClick(replyToEmailId)}
                    sx={{ cursor: "pointer" }}
                  >
                    {replyToEmailSubject || replyToEmailId}
                  </Link>
                </Box>
              </Box>
            )}

            {/* Message content */}
            {messageContent && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  Message Content
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mt: 0.5,
                    maxHeight: 400,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                    fontSize: "0.85rem",
                  }}
                >
                  {messageContent}
                </Paper>
              </Box>
            )}

            {/* Attachments */}
            {resolvedAttachments && resolvedAttachments.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  Attachments
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                  {resolvedAttachments.map((att) => (
                    <Box
                      key={att.attachmentId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <AttachFileIcon fontSize="small" color="action" />
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() =>
                          handleAttachmentClick(att.attachmentId)
                        }
                        sx={{ cursor: "pointer" }}
                      >
                        {att.filename}
                      </Link>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Pipeline Logs */}
            {action.diagnosticEvents?.events?.length ? (
              <Accordion disableGutters defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Pipeline Logs</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <DiagnosticEventsDisplay events={action.diagnosticEvents} />
                </AccordionDetails>
              </Accordion>
            ) : null}

            {/* View Pipeline Result */}
            {action.resultId && (
              <Box sx={{ display: "flex", gap: 1 }}>
                {action.emailId && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() =>
                      dispatch(
                        openResultDrawer({
                          resultId: action.resultId!,
                          input: { emailId: action.emailId! },
                        }),
                      )
                    }
                  >
                    View Pipeline Result
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() =>
                    window.open(
                      `/www/admin/email-automation/result/${action.resultId}`,
                      "_blank",
                    )
                  }
                >
                  Open Result Page
                </Button>
              </Box>
            )}

            {/* Generate Actions Step Logs */}
            {action.generateActionsStepDiagnostics?.diagnosticEvents?.events?.length ? (
              <Accordion disableGutters defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Generate Actions Step Logs</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <DiagnosticEventsDisplay events={action.generateActionsStepDiagnostics.diagnosticEvents} />
                </AccordionDetails>
              </Accordion>
            ) : null}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
